import { ParsedUrlQuery } from 'querystring';
import { safeJsonLd } from '@c/StructuredData';
import { GetServerSideProps } from 'next';
import LandingPageHeader from '@c/landing/LandingPageHeader';
import HeroBanner from '@c/landing/HeroBanner';
import WhyGearFocus from '@c/landing/WhyGearFocus';
import AboutCelebrityBanner from '@c/landing/AboutCelebrityBanner';
import ListingCategories from '@c/landing/ListingCategories';
import HowItWorks from '@c/landing/HowItWorks';
import FAQSection from '@c/landing/FAQSection';
import PromoBanner from '@c/landing/PromoBanner';
import SalesTableSection from '@c/landing/SalesTableSection';
import TestimonialSection from '@c/landing/TestimonialSection';
import LandingPageFooter from '@c/landing/LandingPageFooter';
import GearBagSection from '@c/landing/GearBagSection';
import SEO from '@c/SEO';
import Head from 'next/head';

export default function IndieFilmmakingPage() {
  return (
    <>
      <SEO
        title="Indie Filmmaking Gear - Endorsed by Jeven Dovey"
        description="Shop filmmaking gear endorsed by Jeven Dovey. Verified marketplace for indie filmmakers to buy & sell cameras, lenses, audio, and lighting safely with 48-hour buyer protection."
        ogImage="https://gearfocus.b-cdn.net/landings/indie-filmmaking/jeven-dovey-hero-banner.png"
        keywords={[
          'indie filmmaking gear',
          'filmmaking equipment',
          'Jeven Dovey',
          'buy filmmaking gear',
          'sell camera gear',
          'indie filmmaker marketplace',
          'used cinema cameras',
          'professional filmmaking cameras',
          'cinema lenses',
          'filmmaking audio gear',
          'filmmaking lighting',
          'creator marketplace'
        ]}
      />

      <Head>
        {/* Jeven Dovey Person Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "Person",
              "name": "Jeven Dovey",
              "jobTitle": "Award-Winning Filmmaker and YouTuber",
              "description": "Award-Winning Filmmaker & YouTuber with 2M+ Subscribers Across Platforms, Director, Adventure Filmmaker & Gear Educator",
              "url": "https://gearfocus.com/shop-seller/jeven-dovey-gear",
              "sameAs": [
                "https://www.youtube.com/@jevendovey",
                "https://www.instagram.com/jevendovey"
              ],
              "knowsAbout": [
                "Indie Filmmaking",
                "Cinema Cameras",
                "Professional Video Equipment",
                "Filmmaking Education",
                "Adventure Filmmaking",
                "Content Creation"
              ]
            })
          }}
        />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Indie Filmmaking Gear Marketplace - Endorsed by Jeven Dovey",
              "description": "Shop filmmaking gear endorsed by Jeven Dovey. Verified marketplace for indie filmmakers to buy & sell cameras, lenses, audio, and lighting safely with 48-hour buyer protection.",
              "url": "https://gearfocus.com/lp/indie-filmmaking-gear-marketplace",
              "specialty": "Indie Filmmaking Equipment",
              "about": {
                "@type": "Thing",
                "name": "Indie Filmmaking Equipment"
              },
              "mentions": {
                "@type": "Person",
                "name": "Jeven Dovey"
              }
            })
          }}
        />

        {/* BreadcrumbList Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://gearfocus.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Landing Pages",
                  "item": "https://gearfocus.com/lp"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Indie Filmmaking Gear",
                  "item": "https://gearfocus.com/lp/indie-filmmaking-gear-marketplace"
                }
              ]
            })
          }}
        />

        {/* ItemList Schema for Jeven's Gear Bag */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "name": "Jeven Dovey's Indie Filmmaker Kit",
              "description": "Professional filmmaking equipment trusted by Jeven Dovey for indie productions",
              "itemListElement": [
                {
                  "@type": "Product",
                  "position": 1,
                  "name": "Sony A9III",
                  "description": "Professional mirrorless camera for indie filmmaking",
                  "brand": {
                    "@type": "Brand",
                    "name": "Sony"
                  },
                  "url": "https://gearfocus.com/m/sony-a9iii",
                  "image": "https://gearfocus.com/landings/indie-filmmaking/jeven-gear-sony-a9iii.png",
                  "category": "Camera"
                },
                {
                  "@type": "Product",
                  "position": 2,
                  "name": "Sony FE 16-25mm F2.8 G",
                  "description": "Professional wide-angle zoom lens for versatile filmmaking",
                  "brand": {
                    "@type": "Brand",
                    "name": "Sony"
                  },
                  "url": "https://gearfocus.com/m/sony-fe-16-25mm-f28-g",
                  "image": "https://gearfocus.com/landings/indie-filmmaking/jeven-gear-sony-fe-16-25mm.png",
                  "category": "Lens"
                },
                {
                  "@type": "Product",
                  "position": 3,
                  "name": "DJI Mini Mic",
                  "description": "Compact wireless microphone system for mobile filmmaking",
                  "brand": {
                    "@type": "Brand",
                    "name": "DJI"
                  },
                  "url": "https://gearfocus.com/m/dji-mini-mic",
                  "image": "https://gearfocus.com/landings/indie-filmmaking/jeven-gear-dji-mini-mic.png",
                  "category": "Audio"
                },
                {
                  "@type": "Product",
                  "position": 4,
                  "name": "Ulanzi Carbon Tripod",
                  "description": "Lightweight carbon fiber tripod for stable filmmaking",
                  "brand": {
                    "@type": "Brand",
                    "name": "Ulanzi"
                  },
                  "url": "https://gearfocus.com/m/ulanzi-carbon-tripod",
                  "image": "https://gearfocus.com/landings/indie-filmmaking/jeven-gear-ulanzi-tripod.png",
                  "category": "Accessories"
                }
              ]
            })
          }}
        />
      </Head>

      <LandingPageHeader />

      <HeroBanner
        backgroundImageUrlWeb="/landings/indie-filmmaking/jeven-dovey-hero-banner.png"
        backgroundImageUrlMobile="/landings/indie-filmmaking/jeven-dovey-hero-banner-mobile.png"
        title="Indie Filmmakers:"
        headline="Stop Overpaying for Camera Gear - Buy and Sell Smarter."
        subheadline={<>GearFocus is the filmmaker-friendly marketplace designed specifically for creators, by creators.<br />Endorsed by filmmaker and YouTuber, </>}
        highlightName="Jeven Dovey"
        ctaPrimaryLink={{url: "/sell", target: "_self"}}
        ctaPrimaryLabel="List Gear for Free"
        ctaSecondaryLink={{url: "https://gearfocus.com/shop-seller/jevens-gear-shop-3059", target: "_self"}}
        ctaSecondaryLabel="Shop Jeven's Store"
        ctaBubble="/landings/indie-filmmaking/Jeven-dovey-profile-bubble.png"
        ctaBubbleAlt="Jeven Dovey - Award-winning filmmaker and YouTuber with 2M+ subscribers"
        ratingText="4.7 Excellent on"
        ratingImageUrl="https://gearfocus.b-cdn.net/landings/rating-star.svg"
        ratingImageAlt="4.7 out of 5 star rating from verified customer reviews"
        trustpilotLogoUrl="https://gearfocus.b-cdn.net/landings/trustpilot-logo.svg"
        trustpilotLogoAlt="Trustpilot - Independent review platform"
        trustStatement="Join 10,000+ filmmakers trusting us today!"
      />

      <WhyGearFocus />

      <AboutCelebrityBanner
        heading={`I Don't Gamble with My Productions - or My Budget.`}
        quote={`"GearFocus finally gives indie filmmakers what we've been missing - a safe, trustworthy place to buy and sell without the stress."`}
        name="Jeven Dovey"
        title={`Award-Winning Filmmaker & YouTuber - 2M+ Subscribers Across Platforms - Director, Adventure Filmmaker & Gear Educator`}
        imageUrl="/landings/indie-filmmaking/jeven-dovey-shooting.png"
        imageAlt="Jeven Dovey filming on location with professional Sony cinema camera and gimbal for adventure filmmaking"
      />

      <ListingCategories
        title="Essential Indie Filmmaking Gear to Bring Your Vision to Life"
        items={[
          { src: '/landings/indie-filmmaking/shop-cameras-indie-filmmaking.png', alt: 'Shop cinema cameras', url: '/c/video-camcorders' },
          { src: '/landings/indie-filmmaking/shop-lenses-indie-filmmaking.png', alt: 'Shop cinema lenses', url: '/c/photo-video-lenses' },
          { src: '/landings/indie-filmmaking/shop-video-indie-filmmaking.png', alt: 'Shop audio gear', url: '/c/audio-recording' },
          { src: '/landings/indie-filmmaking/shop-drones-indie-filmmaking.png', alt: 'Shop drones and gimbals', url: '/c/drones-aerial-imaging' },
          { src: '/landings/indie-filmmaking/shop-accessories-indie-filmmaking.png', alt: 'Shop filmmaking accessories', url: '/c/photo-video-accessories' },
        ]}
      />

      <HowItWorks />

      <FAQSection
        faqs={
          [
            {
              question: 'Is GearFocus really safer than eBay or Facebook Marketplace?',
              answer:
                "Yes. Every seller is verified, your payment is held by GearFocus until you sign off on receipt or your 48-hour verification window ends, and if the gear isn't as described you can return it at no charge.",
            },
            {
              question: 'How much does it cost to sell on GearFocus?',
              answer:
                'Listings are free, and we charge the industry\'s lowest total fee (8.5%) only when your gear sells. No hidden fees or surprise charges.',
            },
            {
              question: 'How do I know I\'ll actually get paid?',
              answer:
                "GearFocus holds the buyer's payment and releases it to the seller once the buyer confirms receipt or the 48-hour verification window closes with no dispute.",
            },
            {
              question: 'Can I return something if it\'s not as described?',
              answer:
                'Yes. Buyers have 48 hours to inspect the gear. If it doesn\'t match the listing, we\'ll cover the return and refund you in full.',
            },
            {
              question: 'How long does it take to get paid once my item has sold?',
              answer:
                'Payments are released when the buyer confirms delivery, or automatically 48 hours after confirmed delivery if no issue is raised.',
            },
            {
              question: 'Is GearFocus just for indie filmmakers?',
              answer:
                'Nope! It\'s built for all creators - but this page is tailored specifically to indie filmmakers, since we know how important quality gear is on a budget.',
            }
          ]
        }
      />

      <PromoBanner
        youtubeUrl="https://youtu.be/6uaygGVOjfk?si=PQJuBEgeGC8jT20r"
        useClickableThumbnail={true}
        mainImage="/landings/indie-filmmaking/jeven-dovey-filmmaking-guide-thumbnail.png"
        mainImageAlt="Jeven Dovey's Filmmaking Beginner's Guide - Learn Your Camera in 28 Minutes"
        cornerImage="/landings/indie-filmmaking/jeven-dovey-shooting.png"
        cornerImageAlt="Indie filmmaker Jeven Dovey shooting with professional cinema camera"
        link="https://www.creatorfilmschool.com/"
        linkText="Join Creator Film School"
        header="Want to Learn Filmmaking Like Jeven?"
        subtext="Learn Your Camera Fast"
      />

      <SalesTableSection
        columns={[{title: 'B&H'}, {title: 'eBay'}, {title: 'MPB'}, {title: 'GearFocus', color: '#FFFFFF', bg: '#ED7523'}]}
        rows={[
          {
            product: 'Sony FX3',
            bh: '$2,800',
            ebay: '$3,347',
            mpb: '$3,100',
            gearFocus: '$3,542',
          },
          {
            product: 'Sony A7S III',
            bh: '$2,100',
            ebay: '$2,647',
            mpb: '$2,300',
            gearFocus: '$2,801',
          },
          {
            product: 'Aputure 120D II',
            bh: '$450',
            ebay: '$532',
            mpb: '$390',
            gearFocus: '$563',
          },
          {
            product: 'DJI RS3 Mini',
            bh: 'N/A',
            ebay: '$348',
            mpb: '$290',
            gearFocus: '$368',
          }
        ]}
      />

      <TestimonialSection
        backgroundImage="https://gearfocus.b-cdn.net/landings/paul-skorupskas-bg.jpg"
        quote="Truly the most amazing customer service... and gave me an amazing deal on a high quality camera."
        author="Kyle G."
        platform="Trustpilot"
        platformLogo={{
          src: 'https://gearfocus.b-cdn.net/landings/trustpilot-logo-white.svg',
          alt: 'Trustpilot logo',
          width: 100,
          height: 100,
        }}
      />

      <GearBagSection
        header="What's in Jeven's Indie Filmmaker Kit?"
        items={[
          {
            title: "Sony A9III",
            url: "https://gearfocus.com/m/sony-a9iii",
            src: "/landings/indie-filmmaking/jeven-gear-sony-a9iii.png"
          }, {
            title: "Sony FE 16-25mm F2.8 G",
            url: "https://gearfocus.com/m/sony-fe-16-25mm-f28-g",
            src: "/landings/indie-filmmaking/jeven-gear-sony-fe-16-25mm.png"
          }, {
            title: "DJI Mini Mic",
            url: "https://gearfocus.com/m/dji-mini-mic",
            src: "/landings/indie-filmmaking/jeven-gear-dji-mini-mic.png"
          }, {
            title: "Ulanzi Carbon Tripod",
            url: "https://gearfocus.com/m/ulanzi-carbon-tripod",
            src: "/landings/indie-filmmaking/jeven-gear-ulanzi-tripod.png"
          },
        ]}
        footer="Jeven's essential indie filmmaking kit includes the Sony A9III camera, Sony FE 16-25mm F2.8 G wide-angle lens, DJI Mini Mic for crystal-clear audio, and the Ulanzi Carbon Tripod for stable shots—perfect for run-and-gun productions, vlogs, and narrative work. These tools help creators capture professional-quality content on any budget."
      />

      <LandingPageFooter />
    </>
  );
}

IndieFilmmakingPage.getLayout = function getLayout(page: React.ReactElement) {
  return <div className="min-h-screen bg-white">{page}</div>;
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=60, stale-while-revalidate=300'
  );

  return {
    props: {

    },
  };
};
